import { Signal, signal } from '@angular/core';

/**
 * THE viewer's time zone and clock (Anton 2026-10-10, "local time everywhere").
 *
 * - Every time on screen shows in the viewer's zone, labelled with its abbreviation
 *   ("23:59 CEST"; a bare offset such as "UTC+4" when the zone has no common one).
 * - The zone is the profile's `time_zone` (IANA) when set, else the browser's own.
 *   `V2TimeZoneService` sets it from the signed-in user; everything here reads it by default,
 *   so a pure helper (a page's view builder) needs no injector.
 * - The clock: `v2Now()` is the browser's clock corrected by the server's (`Date` header), so a
 *   countdown or "closed" decision follows Jet Fuel Tenders' clock, not a device set wrong.
 *
 * Pure apart from the two signals; never reads the browser's local-time getters (getHours…):
 * the Karma multi-zone run (`npm run test:tz`) fails any code that does.
 */

export const UTC_ZONE = 'UTC';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const NBSP = ' ';

/** Whether the browser knows `zone` as an IANA time zone. */
export function isTimeZone(zone: string | null | undefined): zone is string {
    if (!zone || typeof zone !== 'string') {
        return false;
    }
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: zone });
        return true;
    } catch {
        return false;
    }
}

/** The browser's own zone ("Europe/Prague"); UTC when it cannot say. */
export function browserTimeZone(): string {
    try {
        const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return isTimeZone(zone) ? zone : UTC_ZONE;
    } catch {
        return UTC_ZONE;
    }
}

const viewerZone = signal<string>(browserTimeZone());
/** Server clock minus browser clock, in ms; 0 until measured. */
const clockOffset = signal<number>(0);

/** The viewer's zone as a signal: a `computed` that formats a time re-runs when it changes. */
export const v2ViewerZoneSignal: Signal<string> = viewerZone.asReadonly();

/** The viewer's zone now. */
export function v2ViewerZone(): string {
    return viewerZone();
}

/** Sets the viewer's zone: the profile's when valid, else the browser's. Returns the zone used. */
export function setV2ViewerZone(zone: string | null | undefined): string {
    const next = isTimeZone(zone) ? zone : browserTimeZone();
    viewerZone.set(next);
    return next;
}

/** The server's clock minus the browser's, in ms (0 when unknown). */
export const v2ClockOffsetSignal: Signal<number> = clockOffset.asReadonly();

export function setV2ClockOffset(ms: number): void {
    clockOffset.set(Number.isFinite(ms) ? Math.round(ms) : 0);
}

/** "Now" by Jet Fuel Tenders' clock: every countdown and open/closed decision uses this. */
export function v2Now(): number {
    return Date.now() + clockOffset();
}

/**
 * The viewer's today as a Date at the browser's local midnight of that calendar day, for code that
 * does calendar arithmetic with the local getters (date pickers, "this year", "days left"): its
 * getFullYear/getMonth/getDate are the viewer's day, by Jet Fuel Tenders' clock.
 */
export function v2ViewerToday(zone: string = v2ViewerZone()): Date {
    const p = v2ZonedParts(new Date(v2Now()), zone);
    return new Date(p.year, p.month, p.day);
}

/** A calendar day and a clock time in one zone; `month` is 0-based, `weekday` 0 = Sunday. */
export interface V2ZonedParts {
    year: number;
    month: number;
    day: number;
    weekday: number;
    hours: number;
    minutes: number;
    seconds: number;
}

const partsFormatters = new Map<string, Intl.DateTimeFormat>();
const nameFormatters = new Map<string, Intl.DateTimeFormat>();

function partsFormatter(zone: string): Intl.DateTimeFormat {
    let formatter = partsFormatters.get(zone);
    if (!formatter) {
        formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: zone, hourCycle: 'h23', weekday: 'short',
            year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric'
        });
        partsFormatters.set(zone, formatter);
    }
    return formatter;
}

function safeZone(zone: string | null | undefined): string {
    return isTimeZone(zone) ? zone : UTC_ZONE;
}

/** `at`'s day and time on the wall clock of `zone` (the viewer's by default). */
export function v2ZonedParts(at: Date, zone: string = v2ViewerZone()): V2ZonedParts {
    const parts: Record<string, string> = {};
    for (const part of partsFormatter(safeZone(zone)).formatToParts(at)) {
        parts[part.type] = part.value;
    }
    return {
        year: Number(parts['year']),
        month: Number(parts['month']) - 1,
        day: Number(parts['day']),
        weekday: Math.max(0, WEEKDAYS.indexOf(parts['weekday'])),
        hours: Number(parts['hour']) % 24,
        minutes: Number(parts['minute']),
        seconds: Number(parts['second'])
    };
}

/** How far `zone`'s wall clock is ahead of UTC at `at`, in minutes (CEST: 120, Los Angeles in summer: -420). */
export function v2ZoneOffsetMinutes(at: Date, zone: string = v2ViewerZone()): number {
    const p = v2ZonedParts(at, zone);
    const wall = Date.UTC(p.year, p.month, p.day, p.hours, p.minutes, p.seconds);
    const whole = at.getTime() - (((at.getTime() % 1000) + 1000) % 1000);
    return Math.round((wall - whole) / 60000);
}

function zoneName(locale: string, zone: string, at: Date): string {
    const key = `${locale}|${zone}`;
    let formatter = nameFormatters.get(key);
    if (!formatter) {
        formatter = new Intl.DateTimeFormat(locale, { timeZone: zone, timeZoneName: 'short' });
        nameFormatters.set(key, formatter);
    }
    return formatter.formatToParts(at).find(part => part.type === 'timeZoneName')?.value ?? '';
}

const BARE_OFFSET = /^(GMT|UTC)[+-]/;

/**
 * The zone's abbreviation at `at`: "CEST", "PDT", "UTC"; the bare offset ("UTC+4", Anton 2026-10-11) when the
 * zone has no common one. European and Atlantic zones take the British names ("CEST", "BST"),
 * which American English writes as offsets.
 */
export function v2ZoneAbbreviation(at: Date, zone: string = v2ViewerZone()): string {
    const z = safeZone(zone);
    const american = zoneName('en-US', z, at);
    if (!BARE_OFFSET.test(american) || !/^(Europe|Atlantic)\//.test(z)) {
        return zeroOffset(american) || 'UTC';
    }
    const british = zoneName('en-GB', z, at);
    return zeroOffset(british && !BARE_OFFSET.test(british) ? british : american);
}

/**
 * Some browsers write London's winter as "GMT+0": that is GMT. Any other bare offset reads
 * "UTC+4" / "UTC+5:30", as the Profile list writes it.
 */
function zeroOffset(name: string): string {
    if (/^GMT[+-]0(:00)?$/.test(name)) {
        return 'GMT';
    }
    return name.replace(/^GMT(?=[+-])/, 'UTC');
}

/** "UTC+2", "UTC-7", "UTC+5:30", "UTC" for an offset in minutes. */
export function v2UtcOffsetText(minutes: number): string {
    if (!minutes) {
        return 'UTC';
    }
    const sign = minutes > 0 ? '+' : '-';
    const abs = Math.abs(minutes);
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    return `UTC${sign}${h}${m ? `:${String(m).padStart(2, '0')}` : ''}`;
}

/** Whether a zone is UTC itself (no offset ever, named "UTC"). */
export function v2IsUtcZone(zone: string): boolean {
    return /^(Etc\/)?(UTC|UCT|Universal|Zulu)$/.test(zone);
}

const DAY = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME = /^(\d{2}):(\d{2})$/;

/**
 * The instant at which `zone`'s wall clock reads `date` `time` ("2026-10-11", "23:59"); null
 * for unreadable input. A time skipped by a clock change (02:30 on a spring-forward night)
 * moves forward with the clock; a repeated one takes its second occurrence.
 */
export function v2ZonedInstant(date: string, time: string, zone: string = v2ViewerZone()): Date | null {
    const d = DAY.exec((date ?? '').trim());
    const t = TIME.exec((time ?? '').trim());
    if (!d || !t) {
        return null;
    }
    const [hours, minutes] = [Number(t[1]), Number(t[2])];
    if (hours > 23 || minutes > 59) {
        return null;
    }
    const wall = Date.UTC(Number(d[1]), Number(d[2]) - 1, Number(d[3]), hours, minutes);
    // An impossible day ("2026-02-30") is unreadable, not quietly the next month.
    if (isNaN(wall) || new Date(wall).getUTCDate() !== Number(d[3])) {
        return null;
    }
    // The offsets a day before and a day after cover any clock change that night, east or west
    // of Greenwich: each gives a candidate instant; keep those whose wall clock reads `wall`.
    const before = wall - v2ZoneOffsetMinutes(new Date(wall - 86_400_000), zone) * 60000;
    const after = wall - v2ZoneOffsetMinutes(new Date(wall + 86_400_000), zone) * 60000;
    const reads = (instant: number) => {
        const p = v2ZonedParts(new Date(instant), zone);
        return Date.UTC(p.year, p.month, p.day, p.hours, p.minutes) === wall;
    };
    const valid = [before, after].filter(reads);
    if (valid.length) {
        // A repeated time (the clock goes back): its second occurrence, the later instant.
        return new Date(Math.max(...valid));
    }
    // A skipped time (the clock goes forward): read with the offset before the change, so it
    // lands after the gap ("02:30" on a spring-forward night is "03:30").
    return new Date(before);
}

/** "2026-10-11T21:59:00Z": an instant as the API takes it, UTC with Z, whole seconds. */
export function v2UtcIso(at: Date): string {
    return `${at.toISOString().slice(0, 19)}Z`;
}

/** "Sun 11 Oct 2026, 21:59 UTC" (long) or "11 Oct 2026, 21:59 UTC" (short) in `zone`, labelled. */
export function v2ZonedText(at: Date, form: 'long' | 'short', zone: string): string {
    const p = v2ZonedParts(at, zone);
    const day = `${p.day}${NBSP}${MONTHS[p.month]}${NBSP}${p.year}`;
    const text = `${day}, ${pad(p.hours)}:${pad(p.minutes)}${NBSP}${v2ZoneAbbreviation(at, zone)}`;
    return form === 'short' ? text : `${WEEKDAYS[p.weekday]}${NBSP}${text}`;
}

/** "Europe/Prague (CEST, UTC+2)", "Asia/Dubai (UTC+4)", "UTC": a zone as the Profile lists it. */
export function v2TimeZoneLabel(zone: string, at: Date = new Date(v2Now())): string {
    if (v2IsUtcZone(zone)) {
        return 'UTC';
    }
    const name = zone.replace(/_/g, ' ');
    const offset = v2UtcOffsetText(v2ZoneOffsetMinutes(at, zone));
    const abbreviation = v2ZoneAbbreviation(at, zone);
    return BARE_OFFSET.test(abbreviation) || abbreviation === offset
        ? `${name} (${offset})`
        : `${name} (${abbreviation}, ${offset})`;
}

/** Every zone the browser knows, plus UTC and `include` (a saved alias), A–Z by label. */
export function v2TimeZoneOptions(include: string | null = null, at: Date = new Date(v2Now()))
    : { value: string; label: string }[] {
    let zones: string[] = [];
    try {
        const intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
        zones = intl.supportedValuesOf ? intl.supportedValuesOf('timeZone') : [];
    } catch {
        zones = [];
    }
    const all = new Set<string>([UTC_ZONE, ...zones]);
    if (isTimeZone(include)) {
        all.add(include);
    }
    const browser = browserTimeZone();
    all.add(browser);
    return [...all]
        .map(value => ({ value, label: v2TimeZoneLabel(value, at) }))
        .sort((a, b) => a.label.localeCompare(b.label, 'en'));
}

function pad(value: number): string {
    return String(value).padStart(2, '0');
}
